import { Check, EllipsisVertical, type LucideIcon } from 'lucide-react'
import {
  type KeyboardEvent,
  type ReactNode,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'

import { positionMenu, type MenuPosition } from '@/lib/help-popover'
import { cn } from '@/lib/utils'

type PortalMenuItem = {
  /** Makes the item one of a set of choices, ticked while it is the one chosen. */
  checked?: boolean
  /** Marks what replaces or removes data, in the danger colour; such an item goes last. */
  danger?: boolean
  disabled?: boolean
  /**
   * Names the run of items this one starts, or sits in. A run of two or more shows its name as a
   * heading under a divider; a lone item shows neither, so an ungrouped item opens the menu.
   */
  group?: string
  /** A muted line under the label, saying what choosing the item does. */
  hint?: string
  Icon?: LucideIcon
  label: string
  onSelect?: () => void
}

type PortalMenuProps = {
  /** A caption above the items, saying what the menu is for. */
  heading?: string
  items: readonly PortalMenuItem[]
  /** The accessible name of the open menu. */
  menuLabel: string
  /**
   * Places the open menu from the trigger's box and the menu's size, such as beside the trigger as
   * a bank tab's menu opens; undefined falls back to under the trigger.
   */
  placeMenu?: (trigger: DOMRect, width: number, height: number) => MenuPosition | undefined
  /** Where the open menu goes, in place of the page: a modal dialog covers everything outside it. */
  portalTarget?: HTMLElement | null
  /** Classes added to the trigger, such as its place in a row. */
  triggerClassName?: string
  /** The trigger's size and colours, in place of the plain ⋮, as a bevelled box in a rail. */
  triggerLook?: string
  /** What the trigger shows in place of ⋮. */
  triggerContent?: ReactNode
  /** The accessible name of the trigger. */
  triggerLabel: string
  /** The trigger's tooltip. */
  triggerTitle?: string
}

const enabledItems = (menu: HTMLElement | null) =>
  Array.from(menu?.querySelectorAll<HTMLButtonElement>('button:not(:disabled)') ?? [])

/**
 * Splits the items into runs that share a group. A run of two or more is read as a group under
 * its heading; every other item stands alone.
 */
const groupRuns = (items: readonly PortalMenuItem[]) => {
  const runs: { group?: string; items: PortalMenuItem[]; start: number }[] = []
  items.forEach((item, index) => {
    const last = runs.at(-1)
    if (item.group && last?.group === item.group) last.items.push(item)
    else runs.push({ group: item.group, items: [item], start: index })
  })
  return runs
}

/**
 * A ⋮ menu for a container that clips its overflow, such as the patch grid or the operator rack.
 * The menu opens in a portal, claims Escape so the view's shortcuts do not also act on it, and
 * closes on scroll rather than following its trigger.
 */
export function PortalMenu({
  heading,
  items,
  menuLabel,
  placeMenu,
  portalTarget,
  triggerClassName,
  triggerContent,
  triggerLook,
  triggerLabel,
  triggerTitle,
}: PortalMenuProps) {
  const [open, setOpen] = useState(false)
  const [position, setPosition] = useState<MenuPosition>({ left: 12, top: 12 })
  const hintId = useId()
  const triggerRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  const close = (restoreFocus: boolean) => {
    setOpen(false)
    if (restoreFocus) triggerRef.current?.focus()
  }

  useLayoutEffect(() => {
    const trigger = triggerRef.current?.getBoundingClientRect()
    const menu = menuRef.current
    if (!open || !trigger || !menu) return
    // Right-aligned under the trigger, which sits at its container's right edge.
    const { offsetHeight, offsetWidth } = menu
    const anchor = {
      bottom: trigger.bottom,
      left: trigger.right - offsetWidth + 8,
      top: trigger.top,
    }
    setPosition(
      placeMenu?.(trigger, offsetWidth, offsetHeight) ??
        positionMenu(anchor, offsetWidth, offsetHeight, innerWidth, innerHeight),
    )
    ;(enabledItems(menu)[0] ?? menu).focus()
  }, [open, placeMenu])

  useEffect(() => {
    if (!open) return
    const closeOnOutsidePointer = (event: PointerEvent) => {
      const target = event.target as Node
      if (!triggerRef.current?.contains(target) && !menuRef.current?.contains(target)) close(false)
    }
    const closeOnEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key !== 'Escape') return
      event.preventDefault()
      close(true)
    }
    // A menu that scrolls itself, as a tall one on a short screen does, stays open.
    const closeOnMove = (event: Event) => {
      if (!menuRef.current?.contains(event.target as Node)) close(false)
    }
    document.addEventListener('pointerdown', closeOnOutsidePointer)
    document.addEventListener('keydown', closeOnEscape)
    window.addEventListener('scroll', closeOnMove, true)
    return () => {
      document.removeEventListener('pointerdown', closeOnOutsidePointer)
      document.removeEventListener('keydown', closeOnEscape)
      window.removeEventListener('scroll', closeOnMove, true)
    }
  }, [open])

  // The arrow keys move between the items that can be chosen, wrapping at either end. The menu sits at
  // the end of the page, so Tab closes it onto its toggle rather than leaving the page.
  const moveFocus = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Tab') {
      event.preventDefault()
      close(true)
      return
    }
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return
    event.preventDefault()
    const choices = enabledItems(menuRef.current)
    if (choices.length === 0) return
    const current = choices.indexOf(document.activeElement as HTMLButtonElement)
    const step = event.key === 'ArrowDown' ? 1 : -1
    choices[(current + step + choices.length) % choices.length].focus()
  }

  // Each item keeps its index across the runs, so its hint ids stay unique in the menu.
  const renderItem = (
    {
      checked,
      danger,
      disabled,
      hint,
      Icon = checked ? Check : undefined,
      label,
      onSelect,
    }: PortalMenuItem,
    index: number,
  ) => (
    <button
      key={label}
      aria-checked={checked}
      aria-describedby={hint ? `${hintId}-hint-${index}` : undefined}
      aria-labelledby={hint ? `${hintId}-label-${index}` : undefined}
      className={cn(
        'flex w-full cursor-pointer items-center gap-2 rounded-sm px-3 py-2 text-left text-sm transition-colors outline-none focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-[var(--crt-led)] disabled:pointer-events-none disabled:opacity-50',
        danger
          ? 'text-destructive hover:bg-destructive hover:text-destructive-foreground focus-visible:bg-destructive focus-visible:text-destructive-foreground'
          : 'hover:bg-accent hover:text-accent-foreground focus-visible:bg-accent focus-visible:text-accent-foreground',
      )}
      disabled={disabled}
      onClick={() => {
        // Focus returns to the trigger first, so a dialog the action opens restores it there.
        close(true)
        onSelect?.()
      }}
      role={checked === undefined ? 'menuitem' : 'menuitemradio'}
      tabIndex={-1}
      type="button"
    >
      {Icon ? (
        <Icon aria-hidden="true" className="size-4 shrink-0" />
      ) : (
        <span aria-hidden="true" className="size-4 shrink-0" />
      )}
      {hint ? (
        <span className="grid">
          <span id={`${hintId}-label-${index}`}>{label}</span>
          <span className="text-xs text-[var(--crt-ink-3)]" id={`${hintId}-hint-${index}`}>
            {hint}
          </span>
        </span>
      ) : (
        <span>{label}</span>
      )}
    </button>
  )

  return (
    <>
      <button
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={triggerLabel}
        className={cn(
          'grid shrink-0 cursor-pointer place-items-center transition-colors focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[var(--crt-led)]',
          triggerLook ??
            'size-6 text-[var(--crt-ink-4)] hover:text-[var(--crt-acc-lt)] aria-expanded:text-[var(--crt-acc-lt)]',
          triggerClassName,
        )}
        onClick={() => setOpen((current) => !current)}
        ref={triggerRef}
        title={triggerTitle}
        type="button"
      >
        {triggerContent ?? <EllipsisVertical aria-hidden="true" className="size-3.5" />}
      </button>
      {open
        ? createPortal(
            <div
              aria-label={menuLabel}
              className="menu-surface fixed z-[100] min-w-40 overflow-y-auto overscroll-contain border-t-2 border-r-2 border-b-2 border-l-2 border-t-[var(--crt-bevel)] border-r-[var(--crt-shadow)] border-b-[var(--crt-shadow)] border-l-[var(--crt-bevel)] bg-[var(--crt-bg-panel2)] p-1 text-[var(--crt-ink)]"
              onBlur={(event) => {
                const next = event.relatedTarget as Node | null
                if (next && !menuRef.current?.contains(next) && next !== triggerRef.current) {
                  close(false)
                }
              }}
              onKeyDown={moveFocus}
              ref={menuRef}
              role="menu"
              style={position}
              tabIndex={-1}
            >
              {heading ? (
                <p
                  aria-hidden="true"
                  className="px-3 pt-1.5 pb-1 text-[11px] tracking-[0.08em] text-[var(--crt-ink-3)] uppercase"
                >
                  {heading}
                </p>
              ) : null}
              {groupRuns(items).map(({ group, items: run, start }) => {
                if (!group || run.length < 2) {
                  return run.map((item, offset) => renderItem(item, start + offset))
                }
                const headingId = `${hintId}-group-${start}`
                return (
                  <div aria-labelledby={headingId} key={headingId} role="group">
                    <p
                      className="mt-1 border-t border-[var(--crt-line)] px-3 pt-1.5 pb-1 text-[11px] tracking-[0.08em] text-[var(--crt-ink-3)] uppercase"
                      id={headingId}
                    >
                      {group}
                    </p>
                    {run.map((item, offset) => renderItem(item, start + offset))}
                  </div>
                )
              })}
            </div>,
            portalTarget ?? document.body,
          )
        : null}
    </>
  )
}
